import { Inject, Injectable } from '@angular/core';

import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { defer, from, Observable, throwError } from 'rxjs';
import { AAACallDataAdapter } from '../../shared/adapters/AAACallDataAdapter';
import { DRR_BASE_HREF } from '../../shared/shared.config';
import { RouteTypes } from '../main.routes';
import { selectIsEligible } from '../member/member.selectors';
import { selectCreateCallTimeout, selectGetActiveCallsTimeout } from '../ui/timeout/timeout.selectors';
import { openMessageDialog } from '../ui/ui.actions';
import { MessageDialogTypes, StepTypes } from '../ui/ui.types';
import { CallDetailsResponseSchema, CallResponseSchema } from './calls.model';
import { AAACallData, AAACallStatus, CallCancelParams, CallDetails, CallDetailsResponse, CallResponse } from './calls.types';
import { RETRY_CONFIG, TIMEOUT_CONFIG, X_AAA_CALL_ID } from '../../shared/services/http-context.tokens';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { HttpContext } from '@angular/common/http';
import { DrrHttpClient } from '../../http/drr-http-client'


@Injectable({
  providedIn: 'root',
})
export class CallsService {

  isEligible$: Observable<boolean> = this.store$.pipe(
    select(selectIsEligible)
  )
  eligible = false;

  getActiveCallsTimeout$: Observable<number> = this.store$.pipe(
    select(selectGetActiveCallsTimeout)
  )
  getActiveCallsTimeout;

  createCallTimeout$: Observable<number> = this.store$.pipe(
    select(selectCreateCallTimeout)
  )
  createCallTimeout;

  constructor(
    private http: DrrHttpClient,
    private store$: Store,
    private callAdapter: AAACallDataAdapter,
    private router: Router,
    @Inject(DRR_BASE_HREF) private drrBaseHref: string
  ) {
    this.isEligible$.subscribe(eligible => {
      this.eligible = eligible
    })
    this.getActiveCallsTimeout$.subscribe(timeout => {
      this.getActiveCallsTimeout = timeout
    })
    this.createCallTimeout$.subscribe(timeout => {
      this.createCallTimeout = timeout
    })
  }

  addCall$(callData: AAACallData): Observable<Pick<CallResponse, 'callKey'>> {
    const data = this.callAdapter.adapt(callData)
    const context = new HttpContext()

    if (this.createCallTimeout > 0) {
      context.set(TIMEOUT_CONFIG, this.createCallTimeout)
    }

    return this.http.post<Pick<CallResponse, 'callKey'>>('bff/roadservice/calls', data, { context }).pipe(
      switchMap((response) => from(CallResponseSchema.validate(response))),
      catchError((error) => {
        if (
          (error?.response?.status === 400 || error?.response?.status === 409) &&
          error?.response?.data?.error === 'Duplicate Call'
        ) {
          this.store$.dispatch(
            openMessageDialog({
              payload: { type: MessageDialogTypes.CALL_DUPLICATE },
            })
          )
        }

        return throwError(error)
      })
    )
  }

  addPartialCall$(callData: AAACallData): Observable<void> {
    const data = this.callAdapter.adapt(callData)
    const context = new HttpContext()
      .set(RETRY_CONFIG, { ...RETRY_CONFIG.defaultValue(), enabled: false })

    return this.http.post<void>('bff/roadservice/calls/partial', data, { context })
  }

  cancelCall$(callCancelParams: CallCancelParams): Observable<void> {
    // request() is the only client method that carries a body on DELETE; its override is
    // untyped, so the cast lives here rather than widening the shared client.
    return this.http.request('DELETE', `bff/roadservice/calls/${callCancelParams.callKey}`, {
      ...(callCancelParams.reason ? { body: { reason: callCancelParams.reason } } : {}),
    })
  }

  getActiveCall$(
    timeout: number = this.getActiveCallsTimeout,
    addCallFailure: boolean = false
  ): Observable<CallDetails | null> {
    return this.getOrRedirectToLocationWithinTimeout$('bff/roadservice/calls/active', timeout, addCallFailure).pipe(
      map((response) => {
        // Backend returns an array but only supports single call - return the first one or null
        const calls = response?.calls || []
        return calls.length > 0 ? calls[0] : null
      })
    )
  }

  getOrRedirectToLocationWithinTimeout$(url: string, timeout: number, addCallFailure = false): Observable<any> {
    const context = new HttpContext()
      .set(RETRY_CONFIG, { ...RETRY_CONFIG.defaultValue(), enabled: false })

    const request$ = this.http.get<any>(url, { context })

    if (!(timeout > 0) || addCallFailure) {
      return request$
    }

    // The timeout is a deadline on how long the user waits, not on the request. A slow
    // lookup moves them along to the location step while the request keeps running, so an
    // active call that arrives late still routes them to the service tracker.
    return defer(() => {
      const deadline = setTimeout(() => this.redirectToLocation(), timeout)
      const cancelDeadline = () => clearTimeout(deadline)

      return request$.pipe(
        tap(cancelDeadline, () => {
          // The response is never coming, so there is nothing left to wait out.
          cancelDeadline()
          this.redirectToLocation()
        }),
        finalize(cancelDeadline),
      )
    })
  }

  getCallDetails$(
    callKey: string,
    retry: boolean
  ): Observable<AAACallStatus> {
    const context = new HttpContext()
      .set(X_AAA_CALL_ID, callKey)
      .set(RETRY_CONFIG, { ...RETRY_CONFIG.defaultValue(), enabled: retry })

    return this.http.get<CallDetailsResponse>(`bff/roadservice/calls/${callKey}`, { context }).pipe(
      tap(response => {
        CallDetailsResponseSchema.validateSync(response)
      }),
      map(response => {
        return this.callAdapter.adaptCallDetailsResponse(response)
      }),
    )
  }

  private redirectToLocation(): void {
    if (this.eligible) {
      this.router.navigate([this.drrBaseHref, RouteTypes.STEPS], {
        queryParams: { step: StepTypes.BREAKDOWN_LOCATION },
      })
    } else {
      this.router.navigate([
        this.drrBaseHref,
        ...RouteTypes.SIGNIN.split('/'),
      ])
    }
  }
}
